import { useEffect, useMemo, useRef, useState } from "react";
import { Karta, Komunikat, Odznaka, Pole, Przycisk } from "../ui.tsx";

/**
 * Literaki — cztery widoki w jednym pliku: lobby, runda, wyniki, koniec.
 *
 * Problem tej gry na telefonie jest jeden i jest znany: **klawiatura zjada
 * połowę ekranu**, a to, na co trzeba patrzeć, to dziewięć liter. Dlatego
 * domyślnym wejściem nie jest pisanie, tylko dotykanie płytek — nic nie
 * zasłania zestawu, bo klawiatura się nie otwiera. Kto woli pisać, dostaje
 * przełącznik.
 */

const TLO_PLYTKI = "url(/scrabble/plytka.png)";

const NAZWA_JEZYKA: Record<string, string> = {
  pl: "po polsku",
  en: "po angielsku",
  es: "po hiszpańsku",
};

/**
 * Powód odrzucenia, w drugiej osobie i bez żargonu. To jest jedyna informacja
 * zwrotna, jaką gracz dostaje w trakcie rundy, więc musi się czytać w locie.
 */
const OPIS_STATUSU: Record<string, string> = {
  "za-krotkie": "za krótkie",
  "zle-litery": "nie z tych liter",
  "spoza-slownika": "nie ma takiego słowa",
  powtorka: "już to masz",
};

export interface LiterakiWpis {
  slowo: string;
  status: string;
  punkty: number;
  duplikat: boolean;
  at: number;
}

export interface LiterakiView {
  faza: "lobby" | "runda" | "wyniki" | "koniec";
  jezyk: string;
  roundNo: number;
  roundCount: number;
  litery: string[];
  deadlineAt: number | null;
  hostId: string;
  players: string[];
  gotowi: string[];
  totals: Record<string, number>;
  historia: Array<{
    numer: number;
    litery: string[];
    wpisy: Record<string, LiterakiWpis[]>;
    punkty: Record<string, number>;
  }>;
  ustawienia: { minDlugosc: number; duplikatProcent: number; roundSeconds: number };
  mojeWpisy: LiterakiWpis[];
  postep: Array<{ gracz: string; przyjete: number; gotowy: boolean }>;
  canStart: boolean;
  /** Tylko od fazy `wyniki` — w trakcie rundy tego klucza nie ma wcale. */
  wpisy?: Record<string, LiterakiWpis[]>;
}

export function Literaki({
  view,
  nicki,
  mojeId,
  onStart,
  onSlowo,
  onGotowe,
  onWroc,
}: {
  view: LiterakiView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onSlowo: (slowo: string) => void;
  onGotowe: (on: boolean) => void;
  onWroc: () => void;
}) {
  switch (view.faza) {
    case "lobby":
      return <Lobby view={view} nicki={nicki} onStart={onStart} />;
    case "runda":
      return <Runda view={view} nicki={nicki} mojeId={mojeId} onSlowo={onSlowo} onGotowe={onGotowe} />;
    default:
      return <Wyniki view={view} nicki={nicki} mojeId={mojeId} onStart={onStart} onWroc={onWroc} />;
  }
}

function Lobby({
  view,
  nicki,
  onStart,
}: {
  view: LiterakiView;
  nicki: Record<string, string>;
  onStart: () => void;
}) {
  return (
    <>
      <Komunikat>
        Wszyscy dostajecie ten sam zestaw {view.litery.length || 9} liter i układacie z niego tyle
        słów, ile zdołacie. Punktuje długość, a słowo, które miał ktoś jeszcze, jest warte{" "}
        {view.ustawienia.duplikatProcent}% wartości.
      </Komunikat>

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Gramy {NAZWA_JEZYKA[view.jezyk] ?? ""}</div>
        <div className="space-y-1">
          {view.players.map((gracz) => (
            <div key={gracz}>{nicki[gracz] ?? "?"}</div>
          ))}
        </div>
      </Karta>

      {view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Zaczynamy
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie.</Komunikat>
      )}
    </>
  );
}

function Runda({
  view,
  nicki,
  mojeId,
  onSlowo,
  onGotowe,
}: {
  view: LiterakiView;
  nicki: Record<string, string>;
  mojeId: string;
  onSlowo: (slowo: string) => void;
  onGotowe: (on: boolean) => void;
}) {
  /** Pozycje płytek złożone w bufor. Pozycja, nie litera — bo litery się powtarzają. */
  const [bufor, setBufor] = useState<number[]>([]);
  const [klawiatura, setKlawiatura] = useState(false);
  const [wpisane, setWpisane] = useState("");
  const rundaRef = useRef(view.roundNo);

  useEffect(() => {
    if (rundaRef.current === view.roundNo) return;
    rundaRef.current = view.roundNo;
    setBufor([]);
    setWpisane("");
  }, [view.roundNo]);

  const slowo = klawiatura ? wpisane : bufor.map((pozycja) => view.litery[pozycja] ?? "").join("");
  const zaKrotkie = [...slowo].length < view.ustawienia.minDlugosc;
  const jestemGotowy = view.gotowi.includes(mojeId);
  const maNe = view.litery.includes("ñ");

  const wyslij = () => {
    if (zaKrotkie) return;
    onSlowo(slowo);
    setBufor([]);
    setWpisane("");
  };

  /*
   * Własna lista rośnie od góry: najnowsze słowo na wierzchu, żeby werdykt
   * pojawiał się tam, gdzie gracz właśnie patrzył.
   */
  const moje = useMemo(() => [...view.mojeWpisy].reverse(), [view.mojeWpisy]);

  return (
    <>
      <Karta>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-tekst-slaby">
            Runda {view.roundNo}/{view.roundCount} · {NAZWA_JEZYKA[view.jezyk] ?? ""}
          </span>
          <Odznaka>od {view.ustawienia.minDlugosc} liter</Odznaka>
        </div>

        <Zestaw
          litery={view.litery}
          runda={view.roundNo}
          zuzyte={klawiatura ? zuzytePrzezNapis(view.litery, wpisane) : bufor}
          onPlytka={(pozycja) => {
            if (klawiatura) return;
            setBufor((poprzedni) =>
              poprzedni.includes(pozycja) ? poprzedni : [...poprzedni, pozycja],
            );
          }}
        />
      </Karta>

      <Karta>
        {klawiatura ? (
          <div className="flex gap-2">
            <Pole
              value={wpisane}
              onChange={(event) => setWpisane(event.target.value)}
              placeholder="Twoje słowo"
              /*
               * Autokorekta wyłączona jest tu **warunkiem działania**, nie
               * ustawieniem gustu: telefon poprawiający „stoł" na „stół"
               * wstawia literę, której w zestawie nie ma, i gracz dostaje
               * „nie z tych liter" za coś, czego nie napisał.
               */
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="send"
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                wyslij();
              }}
            />
            {maNe ? (
              /* Układ polski daje `ñ` przez długie przytrzymanie `n` — ale nie
                 wolno na tym polegać, więc litera dostaje własny przycisk. */
              <Przycisk onClick={() => setWpisane((tekst) => `${tekst}ñ`)}>ñ</Przycisk>
            ) : null}
          </div>
        ) : (
          <div className="min-h-14 flex items-center flex-wrap gap-2">
            {bufor.length === 0 ? (
              <span className="text-tekst-slaby">Dotknij płytek, żeby ułożyć słowo</span>
            ) : (
              bufor.map((pozycja, index) => (
                <button
                  key={`${pozycja}-${index}`}
                  onClick={() => setBufor((poprzedni) => poprzedni.filter((p) => p !== pozycja))}
                  className="w-10 h-12 rounded-lg font-bold text-lg uppercase"
                  style={{
                    backgroundImage: TLO_PLYTKI,
                    backgroundSize: "100% 100%",
                    // Korpus płytki to zdjęcie jasnego drewna, więc napis nie
                    // może chodzić za motywem — w ciemnym po prostu zniknie.
                    color: "var(--plytka-tekst)",
                  }}
                  aria-label={`Cofnij ${view.litery[pozycja] ?? ""}`}
                >
                  {view.litery[pozycja]}
                </button>
              ))
            )}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <Przycisk wariant="glowny" pelna onClick={wyslij} disabled={zaKrotkie}>
            Wyślij
          </Przycisk>
          <Przycisk
            wariant="cichy"
            onClick={() => {
              setKlawiatura(!klawiatura);
              setBufor([]);
              setWpisane("");
            }}
          >
            {klawiatura ? "Płytki" : "Klawiatura"}
          </Przycisk>
        </div>
      </Karta>

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Kto ile ma</div>
        <div className="space-y-2">
          {view.postep.map((wiersz) => (
            <div key={wiersz.gracz} className="flex items-center gap-3">
              <span className="flex-1 truncate text-sm">{nicki[wiersz.gracz] ?? "?"}</span>
              {wiersz.gotowy ? <Odznaka>gotowy</Odznaka> : null}
              <span className="tabular text-sm text-tekst-slaby">{wiersz.przyjete}</span>
            </div>
          ))}
        </div>
      </Karta>

      <div className="space-y-1">
        {moje.map((wpis, index) => (
          <div
            key={`${wpis.slowo}-${wpis.at}-${index}`}
            className="flex items-center justify-between gap-3 px-1"
          >
            <span className={wpis.status === "ok" ? "uppercase" : "uppercase line-through text-tekst-slaby"}>
              {wpis.slowo}
            </span>
            <span className="text-sm text-tekst-slaby">
              {wpis.status === "ok" ? (
                <span className="tabular text-akcent">+{wpis.punkty}</span>
              ) : (
                OPIS_STATUSU[wpis.status] ?? "odrzucone"
              )}
            </span>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 pt-2 bezpieczny-dol bg-tlo">
        {/*
          „Gotowe" to rezygnacja z reszty **swojego** czasu, a nie STOP: rundę
          zamyka dopiero komplet, więc nikogo nie ucina cudza decyzja.
        */}
        <Przycisk
          wariant={jestemGotowy ? "gorny" : "zwykly"}
          pelna
          onClick={() => onGotowe(!jestemGotowy)}
        >
          {jestemGotowy
            ? `Czekamy na ${view.postep.length - view.gotowi.length}`
            : "Gotowe, nie szukam dalej"}
        </Przycisk>
      </div>
    </>
  );
}

/** Które płytki zjada napis wpisany z klawiatury — do przygaszenia zestawu. */
function zuzytePrzezNapis(litery: string[], napis: string): number[] {
  const wolne = litery.map((_, index) => index);
  const zuzyte: number[] = [];
  for (const znak of napis.toLowerCase().normalize("NFC")) {
    const index = wolne.findIndex((pozycja) => litery[pozycja] === znak);
    if (index < 0) continue;
    zuzyte.push(wolne[index] as number);
    wolne.splice(index, 1);
  }
  return zuzyte;
}

/**
 * Zestaw rundy: dziewięć płytek w dwóch rzędach.
 *
 * Płytka zużyta jest **przygaszona, a nie ukryta** — pozycje liter nie mogą się
 * przestawiać w trakcie układania, bo palec celuje w miejsce, nie w literę. To
 * ten sam wniosek, co przy stojaku w Scrabble.
 *
 * Animacja wjazdu jest wyzwalana **numerem rundy, nie zawartością zestawu**:
 * ten sam zestaw może wypaść dwa razy pod rząd i wtedy „nic się nie dzieje" —
 * dokładnie ta sama pułapka, co z literą w Państwach-Miastach i z kością,
 * która wypadła dwa razy.
 */
function Zestaw({
  litery,
  runda,
  zuzyte,
  onPlytka,
}: {
  litery: string[];
  runda: number;
  zuzyte: number[];
  onPlytka: (pozycja: number) => void;
}) {
  const wGorze = Math.ceil(litery.length / 2);
  const rzedy = [litery.slice(0, wGorze), litery.slice(wGorze)];
  let przesuniecie = 0;

  return (
    <div key={runda} className="space-y-2">
      {rzedy.map((rzad, numerRzedu) => {
        const start = przesuniecie;
        przesuniecie += rzad.length;
        return (
          <div key={numerRzedu} className="flex justify-center gap-2">
            {rzad.map((litera, index) => {
              const pozycja = start + index;
              const uzyta = zuzyte.includes(pozycja);
              return (
                <button
                  key={pozycja}
                  onClick={() => onPlytka(pozycja)}
                  disabled={uzyta}
                  aria-label={litera}
                  className={[
                    "w-14 h-14 rounded-lg font-bold text-2xl uppercase transition-opacity",
                    uzyta ? "opacity-30" : "",
                  ].join(" ")}
                  style={{
                    backgroundImage: TLO_PLYTKI,
                    backgroundSize: "100% 100%",
                    color: "var(--plytka-tekst)",
                  }}
                >
                  {litera}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Wyniki rundy i tabela partii.
 *
 * Kolumna na gracza, jego słowa z punktami, duplikaty oznaczone. To ekran, na
 * którym padają pretensje, więc musi tłumaczyć sam siebie bez pytania.
 */
function Wyniki({
  view,
  nicki,
  mojeId,
  onStart,
  onWroc,
}: {
  view: LiterakiView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onWroc: () => void;
}) {
  const koniec = view.faza === "koniec";
  const runda = view.historia[view.historia.length - 1];
  const wpisy = view.wpisy ?? runda?.wpisy ?? {};

  const tabela = view.players
    .map((gracz) => ({ gracz, punkty: view.totals[gracz] ?? 0 }))
    .sort((a, b) => b.punkty - a.punkty);

  return (
    <>
      {runda ? (
        <Karta>
          <div className="text-sm text-tekst-slaby mb-2">
            Zestaw rundy {runda.numer}: {runda.litery.join(" ").toUpperCase()}
          </div>
        </Karta>
      ) : null}

      {view.players.map((gracz) => {
        const moje = wpisy[gracz] ?? [];
        const przyjete = moje.filter((wpis) => wpis.status === "ok");
        return (
          <Karta key={gracz}>
            <div className="flex items-center justify-between mb-2">
              <span className={gracz === mojeId ? "text-akcent font-semibold" : ""}>
                {nicki[gracz] ?? "?"}
              </span>
              <span className="tabular text-sm text-tekst-slaby">
                +{runda?.punkty[gracz] ?? 0} · razem {view.totals[gracz] ?? 0}
              </span>
            </div>
            {przyjete.length === 0 && moje.length === 0 ? (
              <div className="text-sm text-tekst-slaby">nic nie znalazł</div>
            ) : (
              <div className="space-y-1">
                {moje.map((wpis, index) => (
                  <div
                    key={`${wpis.slowo}-${index}`}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span
                      className={
                        wpis.status === "ok" ? "uppercase" : "uppercase line-through text-tekst-slaby"
                      }
                    >
                      {wpis.slowo}
                    </span>
                    <span className="text-tekst-slaby">
                      {wpis.status === "ok" ? (
                        <>
                          {wpis.duplikat ? <span className="mr-2">ktoś to też miał</span> : null}
                          <span className="tabular text-akcent">+{wpis.punkty}</span>
                        </>
                      ) : (
                        OPIS_STATUSU[wpis.status] ?? "odrzucone"
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Karta>
        );
      })}

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">{koniec ? "Koniec partii" : "Razem"}</div>
        <div className="space-y-1">
          {tabela.map((wiersz, miejsce) => (
            <div key={wiersz.gracz} className="flex items-center justify-between">
              <span>
                {miejsce + 1}. {nicki[wiersz.gracz] ?? "?"}
              </span>
              <span className="tabular">{wiersz.punkty}</span>
            </div>
          ))}
        </div>
      </Karta>

      {koniec ? (
        <Przycisk wariant="glowny" pelna onClick={onWroc}>
          Wróć do listy
        </Przycisk>
      ) : view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Następna runda
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie następną rundę.</Komunikat>
      )}
    </>
  );
}
